import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Unit, UnitMembership } from '@/types';
import { useAuth } from './AuthContext';

interface UnitContextType {
  units: Unit[]; // Using Unit type for backward compatibility
  currentUnit: Unit | null;
  setCurrentUnit: (unit: Unit | null) => void;
  loading: boolean;
  refreshUnits: () => Promise<void>;
  fetchUnitById: (unitId: string) => Promise<Unit | null>;
}

const UnitContext = createContext<UnitContextType | undefined>(undefined);

export function useUnit() {
  const context = useContext(UnitContext);
  if (context === undefined) {
    throw new Error('useUnit must be used within a UnitProvider');
  }
  return context;
}

// Legacy export
export const useLodge = useUnit;

export function UnitProvider({ children }: { children: React.ReactNode }) {
  const { currentUser, userProfile } = useAuth();
  const [units, setUnits] = useState<Unit[]>([]);
  const [currentUnit, setCurrentUnit] = useState<Unit | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUnits = async () => {
    // Check for units field first (new), then fall back to lodges (legacy)
    const memberships = userProfile?.units || userProfile?.lodges || [];
    
    if (!userProfile || !memberships?.length) {
      setUnits([]);
      setLoading(false);
      return;
    }

    try {
      // Extract unit IDs from either units or lodges field
      const unitIds: string[] = Array.from(
        new Set(
          memberships
            .map((membership: UnitMembership | null | undefined) => {
              if (membership?.unitId) {
                return membership.unitId;
              }

              if (membership?.lodgeId) {
                return membership.lodgeId;
              }

              return null;
            })
            .filter(
              (unitId: string | null): unitId is string =>
                typeof unitId === 'string' && unitId.length > 0
            )
        )
      );
      const fetchedUnits: Unit[] = [];

      for (const unitId of unitIds) {
        const unitDoc = await getDoc(doc(db, 'units', unitId));
        if (unitDoc.exists()) {
          const data = unitDoc.data();
          fetchedUnits.push({
            id: unitDoc.id,
            name: data.name,
            unitNumber: data.unitNumber || data.number || '',
            members: data.members || [],
            createdAt: data.createdAt?.toDate() || new Date(),
            subscription: data.subscription,
            settings: data.settings || {},
            typeId: data.typeId,
          });
        }
      }

      setUnits(fetchedUnits);
      
      // Set current unit to first one if not already set
      if (!currentUnit && fetchedUnits.length > 0) {
        setCurrentUnit(fetchedUnits[0]);
      }
    } catch (error) {
      console.error('Error fetching units:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshUnits = async () => {
    await fetchUnits();
  };

  const fetchUnitById = async (unitId: string): Promise<Unit | null> => {
    // Only admins can fetch units they're not members of
    if (!userProfile?.isAdmin) {
      // For non-admins, only return if they're a member
      const memberships = userProfile?.units || userProfile?.lodges || [];
      const hasAccess = memberships.some((membership: UnitMembership | null | undefined) => 
        membership?.unitId === unitId || membership?.lodgeId === unitId
      );
      if (!hasAccess) {
        return null;
      }
    }

    try {
      const unitDoc = await getDoc(doc(db, 'units', unitId));
      if (unitDoc.exists()) {
        const data = unitDoc.data();
        return {
          id: unitDoc.id,
          name: data.name,
          unitNumber: data.unitNumber || data.number || '',
          members: data.members || [],
          createdAt: data.createdAt?.toDate() || new Date(),
          subscription: data.subscription,
          settings: data.settings || {},
          typeId: data.typeId,
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching unit by ID:', error);
      return null;
    }
  };

  useEffect(() => {
    if (currentUser && userProfile) {
      fetchUnits();
    } else {
      setUnits([]);
      setCurrentUnit(null);
      setLoading(false);
    }
  }, [currentUser, userProfile]);

  const value: UnitContextType = {
    units,
    currentUnit,
    setCurrentUnit,
    loading,
    refreshUnits,
    fetchUnitById,
  };

  return <UnitContext.Provider value={value}>{children}</UnitContext.Provider>;
}

// Legacy export
export const LodgeProvider = UnitProvider;

